import React, { useCallback, useMemo } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { useSettings } from '@atlas/core';

import { CDN_TYPE_CMS, imageUrlCDN } from 'common/utils/utils';
import NotificationContainer from 'components/NavigationBar/NotificationContainer';
import {
  MENU_ITEM_COUNTER_COMPONENT,
  MENU_ITEMS_WITH_COUNTER,
} from 'src/components/Pages/Menu/MenuList/constants';

export const LinkComponent = ({ children, className, link, target = '' }) => {
  const { pathname, search } = useLocation();
  const active = !!matchPath(pathname, { path: link });

  const parsedQuery = qs.parse(search);
  const isDetailPage = !!parsedQuery.e;
  const isResultDetailPage = !!parsedQuery.r;
  const isSearchPage = !!parsedQuery.q;
  const onClickHandler = useCallback(
    e => {
      if (active && !isDetailPage && !isSearchPage && !isResultDetailPage) {
        e.preventDefault();
      }
    },
    [active, isDetailPage, isSearchPage, isResultDetailPage],
  );

  if (link.startsWith('http')) {
    const linkClassName = cx(className, {
      active: link.includes(pathname),
    });

    return (
      <a className={linkClassName} href={link} onClick={onClickHandler} target={target}>
        {children}
      </a>
    );
  }

  return (
    <NavLink
      activeClassName="active"
      className={className}
      onClick={onClickHandler}
      target={target}
      to={link}
    >
      {children}
    </NavLink>
  );
};

const NavigationBarItem = ({
  altIcon,
  edit = '',
  icon,
  iconType,
  isSupportIcon,
  label,
  lightIcon,
  link,
  onclick = () => {},
  target,
  withNotification,
}) => {
  const { theme } = useSettings();
  const isDarkMode = theme === 'dark';

  const classNameItem = cx('navigation-tab-bar-item', { edit });
  const [forceDefaultIcon, setForceDefaultIcon] = React.useState(false);

  const CounterComponent = useMemo(() => {
    if (MENU_ITEMS_WITH_COUNTER.includes(label)) {
      return MENU_ITEM_COUNTER_COMPONENT[label];
    }

    return null;
  }, [label]);

  const Icon = useMemo(() => {
    if (iconType === 'custom' && !forceDefaultIcon) {
      if (isDarkMode) {
        return (
          <div className="menu-custom-icon">
            <img
              alt="menu-item"
              onError={() => setForceDefaultIcon(true)}
              src={imageUrlCDN(altIcon || lightIcon || icon, CDN_TYPE_CMS)}
            />
            {CounterComponent && <CounterComponent />}
          </div>
        );
      }

      if (isSupportIcon) {
        return (
          <div className="menu-custom-icon">
            <img alt="menu-item" src={imageUrlCDN(icon || altIcon, CDN_TYPE_CMS)} />
          </div>
        );
      }

      return (
        <div className="menu-custom-icon">
          <img
            alt="menu-item"
            onError={() => setForceDefaultIcon(true)}
            src={imageUrlCDN(lightIcon || altIcon || icon, CDN_TYPE_CMS)}
          />
          {CounterComponent && <CounterComponent />}
        </div>
      );
    }

    return (
      <div className="menu-icon">
        <span className={`bottom-${forceDefaultIcon ? 'i-casino' : icon}`} />
        {CounterComponent && <CounterComponent />}
      </div>
    );
  }, [
    iconType,
    forceDefaultIcon,
    icon,
    isDarkMode,
    isSupportIcon,
    lightIcon,
    altIcon,
    CounterComponent,
  ]);

  if (edit) {
    return (
      <div className={classNameItem} onClick={onclick}>
        <div className="icon-container">{Icon}</div>
        <span>{label}</span>
      </div>
    );
  }

  return (
    <LinkComponent className="button navigation-item" link={link} target={target}>
      {Icon}
      <NotificationContainer withNotification={withNotification}>
        <span className="label sub-title-xsmall-500-text">{label}</span>
      </NotificationContainer>
    </LinkComponent>
  );
};

NavigationBarItem.propTypes = {
  altIcon: PropTypes.string,
  edit: PropTypes.string,
  icon: PropTypes.string.isRequired,
  iconType: PropTypes.string.isRequired,
  isSupportIcon: PropTypes.bool,
  label: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  target: PropTypes.string,
  withNotification: PropTypes.bool,
};

export default NavigationBarItem;
